// CORE IMPORTS
import { useEffect, useRef, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import { debounce } from "lodash";
import { Stack, Box, useTheme, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";

// STYLED COMPONENTS
import {
  Chip,
  CryptoSelection,
  FrequencySelectorField,
  SpendLimitField,
  StyledLabel,
} from "../styles/DcaFields";
import { ScheduleDatePicker } from "./ScheduleDatePicker";
import {
  CustomInputField,
  HeaderWrapper,
  InputsWrapper,
  InputWrapper,
  Wrapper,
  SummaryAndButtonWrapper,
} from "../../styles/Trade.styles";

// COMPONENTS
import { TradeSubmitButton } from "../../components/TradeSubmit";
import TradeCoinSelectionButton from "../../components/TradeCoinSelectionButton";
import BasicDatePicker from "./BasicDatePicker";
import BalanceTooltip from "src/components/wallets/header/BalanceTooltip";
import TradeConfirmationModal from "../../components/TradeConfirmationModal";
import { ClearButton } from "../../components/ClearButton";
import { TradeSummary } from "../../components/TradeSummary";
import { TradeError } from "../../components/TradeError";
import { BalanceDisplay } from "../../components/BalanceDisplay";
import { AlertCard, AlertStyleType } from "src/components/alerts/AlertCard";

// REDUX
import {
  resetDcaState,
  setErrorMessage,
  setModalState,
  setSellFiatAmount,
  resetLimitValues,
  setSpendLimitError,
  setSummaryResponse,
  useDcaSlice,
} from "src/store/features/dca.slice";
import api from "src/store/features/api";
import {
  GetDcaStrategySummaryApiResponse,
  DcaStrategyReqDto,
} from "src/store/features/api/generated";
import { useAppDispatch } from "src/store/store";
import { useUserSlice } from "src/store/features/user.slice";

// HOOKS
import usePermission from "src/utils/hooks/usePermission";
import { use2FACheck } from "../../hooks/use2FACheck";

// ICONS
import { Info } from "@mui/icons-material";
import { IOSSwitch } from "../icons/IOSSwitch";

// UTILS
import { NumericFormatCustom, getCurrencyCode } from "../../utils";
import { formatPrice } from "src/components/portfolio/helpers";

// STYLES
import { SwapStyles } from "../../swap/styles/SwapStyles";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// CONSTANTS
import { HELP_TOOLTIP } from "../../constants";

export default function CreateStrategyComponent() {
  const [selectedFiatAvailable, setSelectedFiatAvailable] = useState<number>(0);
  const [frequencyDisplay, setFrequencyDisplay] = useState<string>("");
  const [getStrategySummary, { isLoading }] = api.useGetDcaStrategySummaryMutation();
  const [toggleState, invert] = useReducer((state) => !state, false);
  const dispatch = useAppDispatch();
  const history = useHistory();

  const buyInputRef = useRef<HTMLInputElement | null>(null);

  const { is2FAEnabled, handle2FACheck, TwoFAAlert } = use2FACheck({
    trackingCategory: "Scheduled_Orders",
  });
  const { isGuest } = usePermission();

  const {
    defaultCurrency: { code },
    userHoldings,
  } = useUserSlice();

  const classes = SwapStyles();

  const {
    frequency,
    buyCurrency,
    sellCurrency,
    amount,
    summary,
    startDate,
    endDate,
    spendLimit,
    spendLimitError,
    modalState,
    errorMessage,
  } = useDcaSlice();

  useEffect(() => {
    const sellCurrencyHolding = userHoldings.find(
      (holding) => holding.currencyConfig.code === sellCurrency,
    );
    if (sellCurrencyHolding) {
      const holdingAvailable = sellCurrencyHolding.available;
      setSelectedFiatAvailable(holdingAvailable);
    } else {
      const codeHolding = userHoldings.find((holding) => holding.currencyConfig.code === code);
      if (codeHolding) {
        const holdingAvailable = codeHolding.available;
        setSelectedFiatAvailable(holdingAvailable);
      }
    }
  }, [code, sellCurrency]);

  const [optionalFieldSelection, setOptionalFieldSelection] = useState<
    "End date" | "Spend limit" | null
  >();

  const isButtonDisabled = !buyCurrency || !amount || !frequency || isLoading || !!errorMessage;
  const submitButtonDisabled = isGuest ? false : !!is2FAEnabled && isButtonDisabled;
  const submitButtonText = isGuest ? "Verify Now" : !is2FAEnabled ? "Enable 2FA" : "Schedule Order";

  const checkInsufficientFunds = () => {
    const requiredAmount = Number(amount);
    const availableAmount = Number(selectedFiatAvailable);

    if (requiredAmount > availableAmount) {
      const deficit = (requiredAmount - availableAmount).toFixed(2);

      const errorMessage = `You currently have ${formatPrice(
        availableAmount,
        sellCurrency ?? code,
      )}. Please deposit
          additional ${formatPrice(Number(deficit), sellCurrency ?? code)}
          or adjust your amount to schedule your order.`;

      dispatch(setErrorMessage(errorMessage));
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!amount || amount === 0) {
      setErrorMessage("");
      return;
    }

    if (checkInsufficientFunds()) {
      return;
    }

    setErrorMessage("");
  }, [amount, selectedFiatAvailable, sellCurrency, code]);

  const getSummary = async () => {
    dispatch(setErrorMessage(""));

    if (checkInsufficientFunds()) {
      return;
    }
    // No amount entered
    if (!amount || amount === 0) return dispatch(setErrorMessage("Please enter an amount."));

    // No min amount met
    if (amount && amount < 50)
      return dispatch(
        setErrorMessage(`This order is below the 50 ${sellCurrency} minimum transaction value.`),
      );

    if (
      toggleState &&
      optionalFieldSelection === "Spend limit" &&
      spendLimit !== undefined &&
      spendLimit !== null &&
      spendLimit < amount
    ) {
      return dispatch(
        setSpendLimitError(
          `Your spend limit must be higher than ${amount} ${sellCurrency ?? code}.`,
        ),
      );
    }

    if (amount) {
      const dcaStrategyReqDto: DcaStrategyReqDto = {
        amount,
        frequency,
        startDate,
        buy: {
          code: buyCurrency!,
        },
        sell: {
          code: sellCurrency ?? code,
          units: amount,
        },
        ...(optionalFieldSelection === "End date" ? { endDate } : {}),
        ...(optionalFieldSelection === "Spend limit" ? { spendLimit } : {}),
      };

      await getStrategySummary({ dcaStrategyReqDto })
        .unwrap()
        .then((response: GetDcaStrategySummaryApiResponse) => {
          trackEvent("User requested summary", { ...dcaStrategyReqDto }, "Scheduled_Orders");
          dispatch(setSummaryResponse(response));
        })
        .catch(({ message }) => {
          dispatch(setErrorMessage(message));
        });
    }
  };

  const debouncedGetSummaryRef = useRef(
    debounce(() => {
      getSummary();
    }, 500),
  );

  useEffect(() => {
    debouncedGetSummaryRef.current = debounce(() => {
      getSummary();
    }, 500);
  }, [getSummary]);

  useEffect(() => {
    if (!toggleState) {
      debouncedGetSummaryRef.current.cancel();
      setOptionalFieldSelection(null);
      dispatch(resetLimitValues());
    }
  }, [toggleState, debouncedGetSummaryRef]);

  useEffect(() => {
    if (amount !== 0 && !isLoading) {
      debouncedGetSummaryRef.current();
    }

    return () => {
      debouncedGetSummaryRef.current.cancel();
    };
  }, [
    amount,
    buyCurrency,
    sellCurrency,
    frequency,
    startDate,
    endDate,
    spendLimit,
    debouncedGetSummaryRef,
  ]);

  const clearCreateComponent = () => {
    dispatch(resetDcaState());
    dispatch(setModalState("create plan"));
    if (toggleState) invert();
    trackEvent("User cleared component", {}, "Scheduled_Orders");
  };

  const handleStrategyButtonClick = () => {
    if (isGuest) {
      trackEvent(
        "Guest Tried to schedule an order but was redirected to Verification",
        {},
        "Scheduled_Orders",
      );
      history.push("/application");
      return;
    }

    if (!handle2FACheck()) return;

    if (toggleState && !optionalFieldSelection)
      return dispatch(setSpendLimitError("Please select a limit or toggle off to proceed"));

    if (toggleState && optionalFieldSelection === "Spend limit" && !spendLimit)
      return dispatch(setSpendLimitError("Please enter a Spend Limit"));
    if (toggleState && optionalFieldSelection === "Spend limit" && spendLimit! < amount)
      return dispatch(setSpendLimitError("Spend Limit should be higher than amount"));
    if (toggleState && optionalFieldSelection === "End date" && !endDate)
      return dispatch(setErrorMessage("Please enter an End Date"));

    trackEvent("User requested summary", {}, "Scheduled_Orders");

    dispatch(setModalState("summary"));
  };

  const handleCloseSummary = () => {
    trackEvent("User closed summary", {}, "Scheduled_Orders");
    dispatch(setModalState("create plan"));
  };

  const handleSellFiatAmountChange = (e: any) => {
    const value = (e.target as HTMLInputElement).value;
    dispatch(setSellFiatAmount(Number(value)));
  };

  const theme = useTheme();

  const frequencyGapSize = useMediaQuery(theme.breakpoints.down("sm")) ? "12px" : "24px";

  useEffect(() => {
    switch (frequency) {
      case "Day":
        setFrequencyDisplay("Daily");
        break;
      case "Week":
        setFrequencyDisplay("Weekly");
        break;
      case "Month":
        setFrequencyDisplay("Monthly");
        break;
      case "Quarter":
        setFrequencyDisplay("Quarterly");
        break;
      default:
        setFrequencyDisplay("");
    }
  }, [frequency]);

  if (modalState === "summary" && buyCurrency) {
    return (
      <TradeConfirmationModal
        tradeType="scheduled"
        open={modalState === "summary"}
        sellUnits={`${amount} ${sellCurrency ?? code} of`}
        sellCode={buyCurrency}
        buyUnits={""}
        buyCode={getCurrencyCode(sellCurrency ?? code)}
        priceData={dayjs(startDate).format("DD MMM YYYY")}
        fee={summary?.fee?.amount ?? 3.5}
        onClose={handleCloseSummary}
        frequency={frequencyDisplay}
      />
    );
  }

  return (
    <Wrapper data-testid="create-strategy-component">
      <HeaderWrapper>
        <div />
        <ClearButton
          isLoading={isLoading}
          text={isLoading ? "Calculating" : "Clear"}
          onClick={() => clearCreateComponent()}
        />
      </HeaderWrapper>
      {modalState === "create plan" && (
        <>
          <InputsWrapper>
            <InputWrapper sx={{ borderRadius: "16px 16px 0 0" }} error={!!errorMessage}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <StyledLabel title="Buy" />
                <BalanceDisplay
                  balance={formatPrice(selectedFiatAvailable, sellCurrency ?? code)}
                  showCode={false}
                  isPreFormatted={true}
                />
              </Box>
              <Box
                width="100%"
                display="flex"
                justifyContent="flex-end"
                flexDirection="column"
                alignItems="center">
                <CustomInputField
                  id="buy-input"
                  inputRef={buyInputRef}
                  variant="standard"
                  value={amount}
                  onChange={(e) => handleSellFiatAmountChange(e)}
                  disabled={isLoading}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                    disableUnderline: true,
                    classes: {
                      input: classes.inputStyles,
                    },
                    inputProps: {
                      min: 0,
                      max: 1000000,
                      color: "#fff",
                    },
                    startAdornment: (
                      <TradeCoinSelectionButton
                        code={sellCurrency ?? code}
                        logoURL=""
                        maxTotal=""
                        action="sell"
                        tradeType="scheduled"
                      />
                    ),
                  }}
                />
              </Box>
            </InputWrapper>
            <InputWrapper padding="0" borderTop="none" borderBottom="none" hoverStyles={false}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                height="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flex="3"
                  height="100%"
                  alignItems="center"
                  border="none"
                  borderTop="none"
                  borderBottom="none"
                  borderRight="1px solid #E5E7E8"
                  padding="16px"
                  sx={{
                    "&:hover": {
                      outline: "2px solid #002035",
                      outlineOffset: "-2px",
                    },
                  }}>
                  <StyledLabel title="of" />
                  <CryptoSelection />
                </Box>
                <Box
                  display="flex"
                  flex="2"
                  height="100%"
                  alignItems="center"
                  padding="16px"
                  gap={frequencyGapSize}
                  justifyContent="space-between"
                  sx={{
                    "&:hover": {
                      outline: "2px solid #002035",
                      outlineOffset: "-2px",
                    },
                  }}>
                  <StyledLabel title="Every" />
                  <FrequencySelectorField />
                </Box>
              </Box>
            </InputWrapper>
            <InputWrapper sx={{ borderRadius: "0 0 16px 16px" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <StyledLabel title="Starting" />
                <ScheduleDatePicker source="start date" />
              </Box>
            </InputWrapper>
          </InputsWrapper>

          {/* Add Limit */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row" alignItems="center" gap="12px">
              <StyledLabel title="Add Limit" />
              <BalanceTooltip
                message={
                  "You can add an end date or spend limit to your plan. The plan will automatically stop once your selected spent limit or end date is reached. Click on the toggle to enter a limit."
                }
                contrast>
                <Info style={{ fontSize: "17px" }} />
              </BalanceTooltip>
            </Stack>
            <IOSSwitch onClicked={invert} checked={toggleState} />
          </div>

          {toggleState && ( // if toggle is on display the optional fields
            <>
              {/* End date, Spend limit switch */}
              <Stack direction="row" spacing={1}>
                {["End date", "Spend limit"].map((item, it) => (
                  <Chip
                    key={item + it}
                    selected={optionalFieldSelection === item}
                    onClick={() => {
                      dispatch(setErrorMessage(""));
                      dispatch(setSpendLimitError(""));
                      setOptionalFieldSelection(item as any);
                    }}>
                    {item}
                  </Chip>
                ))}
              </Stack>

              {/* Enter Spend Limit Field */}
              {optionalFieldSelection === "Spend limit" && <SpendLimitField />}
              {/* Enter End Date Field */}
              {optionalFieldSelection === "End date" && <BasicDatePicker source="end date" />}
            </>
          )}

          {/* Holdings Errors placeholder */}
          {errorMessage && <TradeError message={errorMessage} />}
          {spendLimitError && <TradeError message={spendLimitError} />}

          {summary && (
            <SummaryAndButtonWrapper>
              <TradeSummary
                title="You Buy"
                amount={amount.toString()}
                currency={`${sellCurrency ?? code} of ${buyCurrency}`}
                lineItems={[
                  {
                    label: `${frequencyDisplay} Starting`,
                    value: dayjs(startDate).format("DD MMM YYYY"),
                    icon: "clear",
                  },
                ]}
                fee={summary.fee.amount}
                feeTooltip={HELP_TOOLTIP}
                expectedOutput={
                  summary.totalTriggers
                    ? {
                        label: "You receive",
                        value:
                          formatPrice(
                            summary.buy.value * summary.totalTriggers,
                            sellCurrency ?? code,
                          ) + ` of ${buyCurrency}`,
                      }
                    : undefined
                }
                showDivider={!!summary?.totalTriggers}
              />
            </SummaryAndButtonWrapper>
          )}

          <TwoFAAlert />

          {isGuest && (
            <AlertCard
              titleText="Action Required"
              subText={`Please verify your account to access this feature. Verifying your account will allow you to deposit funds and schedule orders. Once your account is verified, go to Settings → Security and toggle on "Enable 2 Factor Authentication" to gain full access to trading features.`}
              styleType={AlertStyleType.TRADE}
            />
          )}

          {/* Create Strategy Button */}
          <TradeSubmitButton
            id="submit-schedule-order-button"
            text={submitButtonText}
            disabled={submitButtonDisabled}
            onClick={handleStrategyButtonClick}
            marginTop="26px"
          />
        </>
      )}
    </Wrapper>
  );
}
